import { useState, useCallback, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Chip } from '@mui/material';
import Actions from '../../../store/Actions';
import { getNumericStringSortComparator } from '../../../utils/sortComparators';
import {
  OTCRow,
  UseOTCColumnsProps,
  UseOTCColumnsReturn,
  DeleteColumnConfirmation,
  DAYS_SINCE_ILLIO_UPDATE,
} from '../types';
import {
  BASE_COLUMN_FIELDS,
  COLUMN_FIELDS,
  COLUMN_WIDTHS,
  DATE_FORMATS,
} from '../OTCConstants';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import {
  formatNumberWithCommas,
  formatPercentageWithCommas,
  parseNumberWithCommas,
} from '../../../utils/numberFormatter';
import { OTCView } from '../../../hooks/useViewManagement/types';
import { CURRENCIES } from '../../../components/DataFeed/Configuration/components/ConfigurationSections/CurrencySection/currencyData';
import { getStatusColor } from '../../../utils/instrumentStatusUtils';

export const useOTCColumns = ({
  otcData,
  autosizeColumns,
  isGroupingEnabled,
  calculatePnL,
  apiRef,
  viewManager,
}: UseOTCColumnsProps): UseOTCColumnsReturn => {
  // Column state
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteColumnConfirmation, setDeleteColumnConfirmation] =
    useState<DeleteColumnConfirmation | null>(null);
  const [hiddenColumnFields, setHiddenColumnFields] = useState<Set<string>>(
    new Set()
  );

  // Create currency options from CURRENCIES array
  const currencyOptions = CURRENCIES.map(c => c.code);

  // Generate columns dynamically from data
  useEffect(() => {
    if (otcData.length === 0) return;

    // Collect all unique column names
    const dateFields = new Set<string>();
    const customLabelFields = new Set<string>();

    // Fields to exclude from display
    const excludedFields = [
      'id',
      '_id',
      'otc_id',
      'nav_prices',
      'groups',
      'isGroup',
      'parentGroupId',
      'parentGroupName',
      'parentGroupFullName',
      'groupId',
      'groupName',
    ];

    otcData.forEach(row => {
      Object.keys(row).forEach(key => {
        // Skip base fields, id fields, and excluded fields
        if (
          !BASE_COLUMN_FIELDS.includes(key) &&
          !excludedFields.includes(key) &&
          key !== COLUMN_FIELDS.PNL &&
          key !== DAYS_SINCE_ILLIO_UPDATE
        ) {
          if (DATE_FORMATS.YYYY_MM_DD_REGEX.test(key)) {
            dateFields.add(key);
          } else {
            // This is a custom label field
            customLabelFields.add(key);
          }
        }
      });
    });

    // Check if field structure or column config has actually changed to prevent unnecessary regeneration
    const currentFieldSignature = JSON.stringify({
      dates: Array.from(dateFields).sort(),
      labels: Array.from(customLabelFields).sort(),
      isGroupingEnabled,
      autosizeColumns,
    });

    const previousFieldSignature =
      columns.length > 0
        ? JSON.stringify({
            dates: columns
              .filter(col => DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field))
              .map(col => col.field)
              .sort(),
            labels: columns
              .filter(
                col =>
                  !BASE_COLUMN_FIELDS.includes(col.field) &&
                  !DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field) &&
                  col.field !== DAYS_SINCE_ILLIO_UPDATE &&
                  col.field !== COLUMN_FIELDS.PNL &&
                  col.field !== 'group_name'
              )
              .map(col => col.field)
              .sort(),
            isGroupingEnabled: columns.some(col => col.field === 'group_name'),
            autosizeColumns: columns[0]?.width === undefined, // Check if columns have autosize config
          })
        : '';

    // Skip regeneration if field structure and config haven't changed
    if (
      currentFieldSignature === previousFieldSignature &&
      columns.length > 0
    ) {
      return;
    }

    // Sort date fields in descending order (newest first)
    const sortedDateFields = Array.from(dateFields).sort((a, b) =>
      b.localeCompare(a)
    );

    // Create the "Days Since Illio Update" column definition
    const daysSinceUpdateColumn: GridColDef = {
      field: DAYS_SINCE_ILLIO_UPDATE,
      headerName: 'Days Since Illio Update',
      ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
      editable: false,
      renderCell: params => {
        // Don't show anything for group rows
        if (params.row.isGroup) return null;

        const value = params.value;
        if (value === null) return null;
        return `${value} days`;
      },
    };

    // Create base columns
    const baseColumns: GridColDef[] = [
      {
        field: COLUMN_FIELDS.INSTRUMENT_STATUS,
        headerName: 'Status',
        ...(autosizeColumns ? {} : { width: 130 }),
        editable: true,
        type: 'singleSelect',
        valueOptions: ['ACTIVE', 'INACTIVE'],
        sortable: true,
        filterable: true,
        renderCell: params => {
          // Don't show status for group rows
          if (params.row.isGroup) return null;

          const status = params.value as string | null;
          if (!status) {
            return null;
          }
          return (
            <Chip
              label={status}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: COLUMN_FIELDS.OTC_TYPE,
        headerName: 'OTC Type',
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.NARROW }),
        editable: false,
      },
      {
        field: COLUMN_FIELDS.ILLIO_NAME,
        headerName: 'Illio Name',
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
        editable: true,
      },
      {
        field: COLUMN_FIELDS.CODE,
        headerName: 'Code',
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
      },
      {
        field: COLUMN_FIELDS.CURRENCY,
        headerName: 'Currency',
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.NARROW }),
        editable: true,
        type: 'singleSelect',
        valueOptions: currencyOptions,
      },
    ];

    // Create custom label columns
    const labelColumns: GridColDef[] = Array.from(customLabelFields).map(
      label => ({
        field: label,
        headerName: label,
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
        editable: true,
        getSortComparator: getNumericStringSortComparator,
      })
    );

    // Create P&L column
    const pnlColumn: GridColDef = {
      field: COLUMN_FIELDS.PNL,
      headerName: 'P&L',
      ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
      editable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: params => {
        // Don't show anything for group rows
        if (params.row.isGroup) return null;

        const value = params.value;
        if (value === null || value === undefined || isNaN(Number(value))) {
          return null;
        }

        const numValue = Number(value);
        const color = numValue > 0 ? 'green' : numValue < 0 ? 'red' : 'inherit';

        return (
          <span style={{ color }}>{formatPercentageWithCommas(numValue)}</span>
        );
      },
    };

    // Add date columns
    const dateColumns: GridColDef[] = sortedDateFields.map(dateField => {
      // Format the date for display: convert from YYYY-MM-DD to DD-MM-YYYY
      const [year, month, day] = dateField.split('-');
      const formattedDate = `${day}-${month}-${year}`;

      return {
        field: dateField, // Keep original format as the field name
        headerName: formattedDate, // Display in DD-MM-YYYY format
        ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
        editable: true,
        type: 'string', // Change from "number" to "string" for date fields
        align: 'left',
        headerAlign: 'left',
        valueFormatter: value => {
          // Don't format date values as numbers - return them as-is
          if (value === null || value === undefined || value === '') {
            return '';
          }

          // Format date values properly
          const stringValue = String(value);

          // If it's already a properly formatted date, return as-is
          if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
            return stringValue; // YYYY-MM-DD format
          }

          // If it's an ISO date string (with time), extract just the date part
          if (/^\d{4}-\d{2}-\d{2}T/.test(stringValue)) {
            return stringValue.split('T')[0]; // Extract YYYY-MM-DD part
          }

          // For other date-like patterns, return as-is
          if (
            /^\d{2}-\d{2}-\d{4}$/.test(stringValue) ||
            /^\d{2}\/\d{2}\/\d{4}/.test(stringValue) ||
            /^\d{4}\/\d{2}\/\d{2}/.test(stringValue)
          ) {
            return stringValue;
          }

          // Only try number formatting if it's clearly a numeric value and not a date
          const parsedValue = parseNumberWithCommas(String(value), true);
          const numericValue =
            typeof parsedValue === 'string'
              ? parseFloat(parsedValue)
              : parsedValue;
          if (!isNaN(numericValue) && isFinite(numericValue)) {
            // Additional check: don't format years as numbers (avoid 2,025 for year 2025)
            if (
              numericValue >= 1900 &&
              numericValue <= 2100 &&
              Number.isInteger(numericValue)
            ) {
              return String(value); // Keep years as-is (like 2025)
            }
            return formatNumberWithCommas(parsedValue, 4, true);
          }

          return String(value);
        },
      };
    });

    // Dedicated group column - only shown when grouping is enabled
    const groupNameColumn: GridColDef = {
      field: 'group_name',
      headerName: 'Group',
      width: autosizeColumns ? undefined : 200,
      flex: autosizeColumns ? undefined : 1.2,
      sortable: false,
      filterable: false,
      pinnable: false, // Prevent user from unpinning
      hideable: false, // Prevent user from hiding
      valueGetter: (value: any, row: any) => {
        // For group rows, show the group name
        if (row.isGroup) {
          return row.groupName;
        }
        // For regular rows, return empty string
        return '';
      },
    };

    // Set the columns in correct order: base columns → group column (if enabled) → custom label columns → P&L → days since update column → date columns
    const newColumns = [
      ...baseColumns,
      ...(isGroupingEnabled ? [groupNameColumn] : []),
      ...labelColumns,
      pnlColumn,
      daysSinceUpdateColumn,
      ...dateColumns,
    ];

    setColumns(newColumns);
  }, [otcData, isGroupingEnabled, autosizeColumns]);

  // Handle adding a new column
  const handleAddColumn = useCallback(
    (columnName: string) => {
      // Check different date formats
      const isYYYYMMDDFormat = DATE_FORMATS.YYYY_MM_DD_REGEX.test(columnName);
      const isDDMMYYYYFormat = DATE_FORMATS.DD_MM_YYYY_REGEX.test(columnName);

      // Determine if it's a date column
      const isDateColumn = isYYYYMMDDFormat || isDDMMYYYYFormat;

      let fieldName = columnName; // Default field name (for server)
      let displayName = columnName; // Default display name

      // Convert dates if needed to get both versions of the date format
      if (isDateColumn) {
        if (isDDMMYYYYFormat) {
          // Convert DD-MM-YYYY to YYYY-MM-DD for data storage
          const [day, month, year] = columnName.split('-');
          fieldName = `${year}-${month}-${day}`;
          displayName = columnName; // Keep the display name as DD-MM-YYYY
        } else if (isYYYYMMDDFormat) {
          // Convert YYYY-MM-DD to DD-MM-YYYY for display only
          const [year, month, day] = columnName.split('-');
          displayName = `${day}-${month}-${year}`;
          fieldName = columnName; // Keep the field name as YYYY-MM-DD
        }
      }

      // Check if the column already exists
      const columnExists = columns.some(col => {
        // For date columns, check both the field name (YYYY-MM-DD) and header name (DD-MM-YYYY)
        if (isDateColumn) {
          return col.field === fieldName || col.headerName === displayName;
        }
        // For non-date columns, just check if the field name matches
        return col.field === columnName;
      });

      if (columnExists) {
        // Show an error message if the column already exists
        showSnackbar({
          title: 'Column already exists',
          type: SnackbarType.error,
          message: `A column with name "${columnName}" already exists.`,
        });
        return;
      }

      // Check if we should auto-hide this new date column
      const shouldHideNewDateColumn =
        isDateColumn &&
        viewManager.activeView &&
        (viewManager.activeView as OTCView).autoShowNewDates === false &&
        (viewManager.activeView as OTCView).originalDateColumns &&
        !(viewManager.activeView as OTCView).originalDateColumns!.includes(
          fieldName
        );

      // Debug logging
      console.log('Adding column debug:', {
        columnName,
        fieldName,
        isDateColumn,
        currentlyAppliedView: viewManager.activeView
          ? {
              name: viewManager.activeView.name,
              autoShowNewDates: (viewManager.activeView as OTCView)
                .autoShowNewDates,
              originalDateColumns: (viewManager.activeView as OTCView)
                .originalDateColumns,
            }
          : null,
        shouldHideNewDateColumn,
      });

      // Separate columns into categories
      const baseColumns = columns.filter(
        col =>
          BASE_COLUMN_FIELDS.includes(col.field) &&
          col.field !== COLUMN_FIELDS.PNL
      );

      const pnlColumn = columns.find(col => col.field === COLUMN_FIELDS.PNL);
      const daysSinceColumn = columns.find(
        col => col.field === DAYS_SINCE_ILLIO_UPDATE
      );

      const dateColumns = columns.filter(col =>
        DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field)
      );

      const labelColumns = columns.filter(
        col =>
          !BASE_COLUMN_FIELDS.includes(col.field) &&
          !DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field) &&
          col.field !== DAYS_SINCE_ILLIO_UPDATE &&
          col.field !== COLUMN_FIELDS.PNL &&
          col.field !== 'group_name'
      );

      if (isDateColumn) {
        // Create column definition for date
        const newColumn: GridColDef = {
          field: fieldName,
          headerName: displayName,
          ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
          editable: true,
          align: 'left',
          headerAlign: 'left',
          getSortComparator: getNumericStringSortComparator,
        };

        // Add it to date columns and sort
        const updatedDateColumns = [...dateColumns, newColumn].sort((a, b) =>
          b.field.localeCompare(a.field)
        );

        // Create updated columns with fixed position for the days since column
        const updatedColumns = [
          ...baseColumns,
          ...(isGroupingEnabled
            ? [columns.find(col => col.field === 'group_name')].filter(Boolean)
            : []),
          ...labelColumns,
          pnlColumn as GridColDef,
          daysSinceColumn as GridColDef, // Always place days since column after PNL
          ...updatedDateColumns,
        ];

        setColumns(updatedColumns);

        // Auto-hide the new date column if needed
        if (shouldHideNewDateColumn) {
          console.log('Auto-hiding new date column:', fieldName);

          // Update hidden fields state immediately
          setHiddenColumnFields(prev => {
            const newHiddenFields = new Set(prev);
            newHiddenFields.add(fieldName);
            return newHiddenFields;
          });

          // Apply visibility to grid with a delay to ensure column is added first
          setTimeout(() => {
            if (apiRef.current) {
              const currentVisibility =
                apiRef.current.getColumnVisibilityModel() || {};
              const newVisibility = {
                ...currentVisibility,
                [fieldName]: false,
              };
              console.log('Applying visibility:', newVisibility);
              apiRef.current.setColumnVisibilityModel(newVisibility);

              showSnackbar({
                title: 'New Date Column Hidden',
                type: SnackbarType.info,
                message: `New date column "${displayName}" was automatically hidden because the current view has auto-show new dates disabled.`,
              });
            }
          }, 200);
        }
      } else {
        // For non-date columns (label columns)
        const newColumn: GridColDef = {
          field: columnName,
          headerName: columnName,
          ...(autosizeColumns ? {} : { width: COLUMN_WIDTHS.DEFAULT }),
          editable: true,
          getSortComparator: getNumericStringSortComparator,
        };

        // For non-date columns, add to the existing label columns
        const updatedColumns = [
          ...baseColumns,
          ...(isGroupingEnabled
            ? [columns.find(col => col.field === 'group_name')].filter(Boolean)
            : []),
          ...labelColumns, // Existing label columns
          newColumn, // Add new label column after existing ones
          pnlColumn as GridColDef,
          daysSinceColumn as GridColDef, // Always place days since column after PNL
          ...dateColumns, // Date columns
        ];

        setColumns(updatedColumns);
      }

      // Close the modal
      setModalOpen(false);

      showSnackbar({
        title: 'Column added',
        type: SnackbarType.success,
        message: `Column "${displayName}" has been added successfully.`,
      });
    },
    [columns, isGroupingEnabled, calculatePnL]
  );

  // Handle delete column click
  const handleDeleteColumnClick = useCallback(
    (columnField: string, columnName: string) => {
      setDeleteColumnConfirmation({
        open: true,
        columnField,
        columnName,
      });
    },
    []
  );

  // Handle confirm delete column
  const handleConfirmDeleteColumn = useCallback(async () => {
    if (!deleteColumnConfirmation) return;

    try {
      await Actions.deleteOTCColumn(deleteColumnConfirmation.columnField);

      // Update columns state
      setColumns(prevColumns =>
        prevColumns.filter(
          col => col.field !== deleteColumnConfirmation.columnField
        )
      );

      showSnackbar({
        title: 'Success',
        type: SnackbarType.success,
        message: `Column "${deleteColumnConfirmation.columnName}" has been deleted`,
      });

      setDeleteColumnConfirmation(null);
    } catch (error) {
      console.error('Error deleting column:', error);
      showSnackbar({
        title: 'Error',
        type: SnackbarType.error,
        message: 'Failed to delete column. Please try again.',
      });
    }
  }, [deleteColumnConfirmation]);

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback(
    (newVisibility: Record<string, boolean>) => {
      const newHiddenFields = new Set<string>();

      // Identify which columns are now hidden
      Object.entries(newVisibility).forEach(([field, isVisible]) => {
        if (!isVisible && !(field === 'group_name' && isGroupingEnabled)) {
          // Prevent hiding group_name when groups are enabled
          newHiddenFields.add(field);
        }
      });

      setHiddenColumnFields(newHiddenFields);
    },
    [isGroupingEnabled]
  );

  // Get current date columns
  const getCurrentDateColumns = useCallback((): string[] => {
    return columns
      .filter(col => DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field))
      .map(col => col.field)
      .sort();
  }, [columns]);

  return {
    columns,
    modalOpen,
    deleteColumnConfirmation,
    hiddenColumnFields,
    setColumns,
    setModalOpen,
    setHiddenColumnFields,
    handleAddColumn,
    handleDeleteColumnClick,
    handleConfirmDeleteColumn,
    handleColumnVisibilityChange,
    getCurrentDateColumns,
    setDeleteColumnConfirmation,
  };
};
